/* eslint-disable @next/next/no-img-element */
import React, { useRef, useState, useEffect, useContext } from "react";
import { Card } from "../../../../shadcn/components/ui/card";
import { ResidentialPropertyCard } from "../../../../shadcn/components/custom/ResidentialPropertyCard";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../shadcn/components/ui/tabs";
import { Context, getSearchResults, getWatermarkedImgixUrl } from "services";
import { DateTime } from "luxon";

interface ResidentialPropertyItems {
  title: string;
  value: string;
  children?: {
    className?: string;
    tag?: string;
    subAssetType?: string;
    price?: string;
    address?: string;
    auctionDate?: string;
    bankName?: string;
    bankLogo?: React.ReactNode;
    propertyId?: string;
    imageLink?: string;
    propertyLink?: string;
    propertyDateType?: string;
  }[];
}

export function ResidentialMenu() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { searchForm }: any = useContext(Context);
  const [residentialProperties, setResidentialProperties] = useState<
    ResidentialPropertyItems[]
  >([]);
  const init = async () => {
    const data: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: "*",
          query_by: "",
          facet_by: "dump.asset.sub_asset_type_slug",
          facet_return_parent: "dump.asset.sub_asset_type_slug",
          filter: "true",
          filter_by: `status:active && dump.asset.asset_type_slug:residential ${
            searchForm?.value?.city?.label === "All Cities"
              ? ""
              : `&& dump.location.city_name:${searchForm?.value?.city?.label}`
          }`,
          max_facet_values: "500",
          per_page: 250,
          sort_by: "auction_start_date_unix_timestamp:asc",
        },
        {
          collection: "properties",
          q: "*",
          query_by: "",
          facet_by: "dump.asset.sub_asset_type_slug",
          facet_return_parent: "dump.asset.sub_asset_type_slug",
          filter: "true",
          filter_by: `status:active && dump.asset.asset_type_slug:!=residential ${
            searchForm?.value?.city?.label === "All Cities"
              ? ""
              : `&& dump.location.city_name:${searchForm?.value?.city?.label}`
          }`,
          max_facet_values: "500",
          per_page: 250,
          sort_by: "auction_start_date_unix_timestamp:asc",
        },
      ],
    });

    const mapHitsToChildren = (hits: any[]) => {
      return (
        hits.map((hit: any, index: number) => ({
          tag: hit.document.featured ? "featured" : "", // SHORTLISTED BY 250+
          subAssetType: hit.document.dump.asset.sub_asset_type_name,
          price: hit.document.reserve_price,
          address: hit.document.dump.location.locality_name
            ? `${hit.document.dump.location.locality_name}, ${hit.document.dump.location.city_name}`
            : hit.document.dump.location.city_name,
          imageLink: hit.document?.dump?.property_image
            ? getWatermarkedImgixUrl(
                `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${hit.document?.dump?.property_image}`,
              )
            : hit.document?.dump?.asset?.placeholder_image
            ? getWatermarkedImgixUrl(
                `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${hit?.document?.dump?.asset?.placeholder_image}`,
              )
            : `/residential${(index % 3) + 1}.png`,
          auctionDate: DateTime.fromISO(hit.document.auction_start_date_time)
            .setZone("Asia/Kolkata")
            .toFormat("dd MMMM yyyy"),
          bankName: hit?.document?.dump?.bank?.bank_name,
          bankLogo: hit?.document?.dump?.bank?.bank_logo ? (
            <img
              src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${hit.document.dump.bank.bank_logo}?w=22&h=22&dpr=2&auto=format`}
              alt={`${hit?.document?.dump?.bank?.bank_name} Logo`}
            />
          ) : (
            <p className="bg-brand-orange rounded-full flex justify-center items-center h-6 w-6 font-openSans font-semibold uppercase text-web-body-sm leading-4 text-white py-2">
              {hit?.document?.dump?.bank?.bank_name?.charAt(0)}
            </p>
            // <HDFCLogo height="22px" width="22px" />
          ),
          propertyLink: `/auction-properties/${hit.document.listing_id}`,
          propertyId: hit.document.listing_id,
          propertyDateType: hit.document.property_date_type,
        })) || []
      );
    };

    const filteredPropertiesData = [
      {
        title: "All",
        value: "all",
        children: mapHitsToChildren(
          data.results[0]?.hits?.length > 0
            ? data.results[0].hits
            : data.results[1]?.hits || [],
        ),
      },
      ...(data.results[0]?.facet_counts?.[0]?.counts.map((facet: any) => {
        const hits = data.results[0]?.hits.length
          ? data.results[0].hits
          : data.results[1]?.hits || [];
        const children = hits.filter(
          (hit: any) =>
            hit.document.dump.asset.sub_asset_type_slug ===
            facet.parent.sub_asset_type_slug,
        );

        return {
          title: facet.parent.sub_asset_type_name,
          value: facet.parent.sub_asset_type_slug,
          children: mapHitsToChildren(children),
        };
      }) || []),
    ];

    setResidentialProperties(filteredPropertiesData);
  };

  useEffect(() => {
    (async () => {
      await init();
    })();
  }, [searchForm.value.city]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const updateScrollPosition = () => {
      const scrollPercentage =
        2.3 *
        (scrollContainer.scrollLeft /
          (scrollContainer.scrollWidth - scrollContainer.clientWidth)) *
        100;
      scrollContainer.style.setProperty(
        "--scroll-position",
        `${scrollPercentage}%`,
      );
    };

    scrollContainer.addEventListener("scroll", updateScrollPosition);
    return () =>
      scrollContainer.removeEventListener("scroll", updateScrollPosition);
  }, [residentialProperties]);

  return (
    <Tabs defaultValue="all" className="w-full">
      <div className="overflow-auto scrollbar-hide">
        <TabsList className="w-[613px] lg:w-[714px]  gap-[21px] lg:gap-[50px] bg-white  font-openSans font-normal text-web-body-md lg:text-web-body-lg leading-4 text-[#A8A8A8]">
          {residentialProperties.map((item, index) => (
            <TabsTrigger
              value={item?.value}
              className="capitalize"
              key={`residential_tab_${item.value}_${index}`}
            >
              {item?.title}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {residentialProperties.map((item, index) => (
        <TabsContent
          className="mt-6 lg:mt-9"
          value={item.value}
          key={`residential_tab_trigger_${item.value}_${index}`}
        >
          <Card className="border-none shadow-none relative">
            <div
              ref={scrollContainerRef}
              className="flex overflow-x-auto pb-1 lg:grid lg:grid-cols-3 gap-6 custom-scrollbar"
            >
              {item?.children?.slice(0, 3).map((card, index) => (
                <div
                  key={`residential_card_${card?.subAssetType}_${index}`}
                  className={`bg-white w-full min-w-[320px] lg:min-w-0  hover:scale-[1.01] hover:duration-300 hover:ease-out`}
                >
                  <ResidentialPropertyCard
                    className={card?.className}
                    subAssetType={card?.subAssetType}
                    tag={card?.tag}
                    price={card?.price}
                    address={card?.address}
                    auctionDate={card?.auctionDate}
                    bankName={card?.bankName}
                    bankLogo={card?.bankLogo}
                    propertyId={card?.propertyId}
                    imageLink={card?.imageLink}
                    propertyLink={card?.propertyLink}
                    propertyDateType={card?.propertyDateType}
                  />
                </div>
              ))}
              <div
                className="lg:hidden absolute bottom-4
              w-[90px] h-2 bg-gray-200 custom-scrollbar-track"
              />
            </div>
          </Card>
        </TabsContent>
      ))}
    </Tabs>
  );
}
